import { TOP_LEVEL_TOT_TEST_IDS } from '@/top-level-tot/constants';
import { PaymentPeriodDateRange, ReportingPeriod } from '@/top-level-tot/types';
import {
  listSelectablePaymentPeriods,
  mostRecentCompletedPaymentPeriod,
  paymentPeriodDateRangeFromISODate,
} from '@/top-level-tot/utils/date-utils';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectPicker } from 'rsuite';

type PeriodSelectorProps = {
  reportingPeriodType: ReportingPeriod;
  period: PaymentPeriodDateRange;
  setPeriod: React.Dispatch<React.SetStateAction<PaymentPeriodDateRange>>;
};

export function PeriodSelector(props: PeriodSelectorProps) {
  const { period, reportingPeriodType, setPeriod } = props;

  const [internalValue, setInternalValue] =
    useState<PaymentPeriodDateRange>(period);

  /**
   * @param selectedPeriod ISO 8601 date string of the start of the payment period.
   * It is specifically the start of the payment period due to the nature of the values
   * provided by "listSelectablePaymentPeriods".
   */
  const onPeriodChange = useCallback(
    (selectedPeriod: string | null) => {
      if (!selectedPeriod) return;
      const paymentPeriodDateRange = paymentPeriodDateRangeFromISODate(
        reportingPeriodType,
        selectedPeriod
      );

      setPeriod(paymentPeriodDateRange);
    },
    [reportingPeriodType, setPeriod]
  );

  const selectablePaymentPeriods = useMemo(
    () =>
      listSelectablePaymentPeriods(
        reportingPeriodType,
        mostRecentCompletedPaymentPeriod(reportingPeriodType)
      ),
    [reportingPeriodType]
  );

  const debouncedOnChange = useMemo(
    () =>
      debounce((value: string | null) => {
        onPeriodChange(value);
      }, 250),
    [onPeriodChange]
  );

  const onDropdownSelect = useCallback(
    (value: string | null) => {
      if (!value) return;

      const paymentPeriodDateRange = paymentPeriodDateRangeFromISODate(
        reportingPeriodType,
        value
      );

      setInternalValue(paymentPeriodDateRange);
      debouncedOnChange(value);
    },
    [debouncedOnChange, reportingPeriodType, setInternalValue]
  );

  /**
   * Returning the cancel method, so that when the component is unmounted
   * then all residual debounced calls will be cleaned up.
   */
  useEffect(() => debouncedOnChange.cancel, [debouncedOnChange]);

  return (
    <SelectPicker
      data-testid={TOP_LEVEL_TOT_TEST_IDS.PAYMENT_PERIOD_DROPDOWN}
      data={selectablePaymentPeriods}
      value={internalValue.startDate}
      onChange={onDropdownSelect}
      cleanable={false}
    />
  );
}
